import React from "react";
import type { DOMNode, Element } from "html-react-parser-v6";
import { VOID, cleanAttributes, insideLink, isLink, tagKind } from "./text.sanitize";

/* Server side, no client directive. Turns a CMS `<tooltip>` body into the React
   nodes Text hands Tooltip as its children, so the HTML is parsed and sanitized
   here and Tooltip's own chunk holds no parser. What may stay is decided in
   text.sanitize. The result is plain elements and string props: it crosses the
   server/client boundary as Tooltip's children. */

/* By domhandler's `type`, not instanceof: instanceof matched no node under Jest.
   script and style carry their own `type`, so they never pass this. */
const isTag = (node: DOMNode): node is Element => node.type === "tag";

/* The cleaned body as a small tree. Text is a mutable cell so whitespace can be
   collapsed across element boundaries (an unwrapped tag leaves two neighbouring
   strings) before anything becomes a React node. */
interface Cell {
  text: string;
}
interface Item {
  name: string;
  attributes: Record<string, string>;
  children: Array<Cell | Item>;
  newTab?: boolean;
}
type Piece = Cell | Item;

const isCell = (piece: Piece): piece is Cell => "text" in piece;

/* An element the author hid: `hidden`, or aria-hidden="true". Neither survives
   the allowlist (a block is redrawn as a span, an inline tag loses both), so its
   words would show; dropped whole here, as a reader skips them. */
function isHidden(node: Element): boolean {
  return "hidden" in (node.attribs ?? {}) || node.attribs?.["aria-hidden"] === "true";
}

function textOf(nodes: DOMNode[] | undefined): string {
  return (nodes ?? [])
    .map(node => {
      if (node.type === "text") {
        return node.data;
      }
      if (!isTag(node)) {
        return "";
      }
      const kind = tagKind(node.name);
      if (kind === "drop" || isHidden(node)) {
        return "";
      }
      const inner = textOf(node.children as DOMNode[]);
      return kind === "block" ? ` ${inner} ` : inner;
    })
    .join("");
}

function build(nodes: DOMNode[] | undefined): Piece[] {
  const pieces: Piece[] = [];
  for (const node of nodes ?? []) {
    if (node.type === "text") {
      pieces.push({ text: node.data });
      continue;
    }
    if (!isTag(node)) {
      continue;
    }
    const children = node.children as DOMNode[];
    // A tooltip cannot hold a tooltip: the inner one is only its words.
    if (node.name === "tooltip") {
      pieces.push({ text: textOf(children) });
      continue;
    }
    const kind = tagKind(node.name);
    if (kind === "drop" || isHidden(node)) {
      continue;
    }
    // The panel is a <span> inside the copy's <p>, so blocks keep their words only.
    if (kind === "block") {
      pieces.push({ text: " " }, ...build(children), { text: " " });
      continue;
    }
    // A link with no safe destination, or inside another link, is just its words.
    if (kind === "unwrap" || (node.name === "a" && (!isLink(node) || insideLink(node)))) {
      pieces.push(...build(children));
      continue;
    }
    const attributes = cleanAttributes(node);
    // An image with no safe src would draw an empty box: only its alt words stay.
    if (node.name === "img" && !attributes.src) {
      pieces.push({ text: attributes.alt ?? "" });
      continue;
    }
    // No alt means decorative, not unnamed: an empty alt keeps it out of the reading order.
    if (node.name === "img" && attributes.alt === undefined) {
      attributes.alt = "";
    }
    pieces.push({
      name: node.name,
      attributes,
      children: VOID.has(node.name) ? [] : build(children),
      newTab: node.name === "a" && attributes.target === "_blank"
    });
  }
  return pieces;
}

/* HTML whitespace only: \s would also flatten a no-break space the author chose.
   Runs collapse across element boundaries only where no tag stands between the
   two spaces, and the copy's own first and last space is trimmed. */
function collapse(pieces: Piece[]): void {
  const cells: Cell[] = [];
  // A barrier (null) stands for any element tag: a space either side of one stays.
  const order: Array<Cell | null> = [];
  const walk = (list: Piece[]) => {
    for (const piece of list) {
      if (isCell(piece)) {
        cells.push(piece);
        order.push(piece);
      } else {
        order.push(null);
        walk(piece.children);
        order.push(null);
      }
    }
  };
  walk(pieces);
  let previousEndsSpace = false;
  for (const entry of order) {
    if (entry === null) {
      previousEndsSpace = false;
      continue;
    }
    let text = entry.text.replace(/[ \t\n\r\f]+/g, " ");
    if (previousEndsSpace) {
      text = text.replace(/^ /, "");
    }
    entry.text = text;
    previousEndsSpace = text.endsWith(" ") || (text === "" && previousEndsSpace);
  }
  if (order[0] !== null && order[0] !== undefined) {
    order[0].text = order[0].text.replace(/^ /, "");
  }
  const last = order[order.length - 1];
  if (last) {
    last.text = last.text.replace(/ $/, "");
  }
}

function hasImage(pieces: Piece[]): boolean {
  return pieces.some(piece => !isCell(piece) && (piece.name === "img" || hasImage(piece.children)));
}

function hasFollowableLink(pieces: Piece[]): boolean {
  return pieces.some(
    piece => !isCell(piece) && (piece.name === "a" || hasFollowableLink(piece.children))
  );
}

function textFrom(pieces: Piece[]): string {
  return pieces.map(piece => (isCell(piece) ? piece.text : textFrom(piece.children))).join("");
}

function toNodes(pieces: Piece[], keyPrefix: string): React.ReactNode[] {
  return pieces
    .filter(piece => !isCell(piece) || piece.text !== "")
    .map((piece, index) => {
      if (isCell(piece)) {
        return piece.text;
      }
      const key = `${keyPrefix}${index}`;
      const inner = toNodes(piece.children, `${key}.`);
      const attributes = { ...piece.attributes };
      const cue = " (opens in a new tab)";
      /* An authored aria-label replaces the link's words as its name, so the
         cue joins the label; otherwise it is hidden text after the words. */
      if (piece.name === "a" && piece.newTab && attributes["aria-label"] !== undefined) {
        if (!/opens in a new tab/i.test(attributes["aria-label"])) {
          attributes["aria-label"] += cue;
        }
      } else if (piece.name === "a" && piece.newTab && !/opens in a new tab/i.test(textFrom(piece.children))) {
        inner.push(
          <span className="dds-sr-only" key={`${key}.cue`}>
            {cue}
          </span>
        );
      }
      return React.createElement(
        piece.name,
        {
          ...attributes,
          key
        },
        ...(VOID.has(piece.name) ? [] : inner)
      );
    });
}

export interface TooltipPanel {
  /** The sanitized body, ready to pass to Tooltip as its children. */
  node: React.ReactNode;
  /** A followable link is in it, so Tooltip must be a disclosure. */
  hasLink: boolean;
}

/**
 * A `<tooltip>` tag's body as the Tooltip's rich content, or null when there is
 * nothing to say (no words and no image). Safe elements only, with allowlisted
 * attributes; a new-tab link gets its `rel` and a visually hidden cue.
 */
export function tooltipPanel(tag: Element): TooltipPanel | null {
  const children = tag.children as DOMNode[];
  const pieces = build(children);
  collapse(pieces);
  // From the built pieces, so an image's alt words count and hidden ones do not.
  if (!textFrom(pieces).trim() && !hasImage(pieces)) {
    return null;
  }
  const nodes = toNodes(pieces, "");
  return {
    node: nodes.length === 1 && typeof nodes[0] === "string" ?
      nodes[0] :
      React.createElement(React.Fragment, null, ...nodes),
    hasLink: hasFollowableLink(pieces)
  };
}
